/**
 * Utility functions for managing authentication state cleanup
 */

import { clearTrialReminderFlag } from "../../../utils/trialReminderUtils";

// Global logout callback to notify UserContext
let globalLogoutCallback = null;

/**
 * Set global logout callback (to be called by UserContext)
 */
export const setGlobalLogoutCallback = (callback) => {
  globalLogoutCallback = callback;
};

/**
 * Clear all frontend authentication data (cookies and localStorage)
 * This is a standalone function that can be used outside of UserContext
 */
export const clearFrontendAuthData = () => {
  console.log("🧹 AuthUtils: Clearing all authentication data");

  // Clear localStorage authentication data
  localStorage.removeItem("authToken");
  localStorage.removeItem("groupId");
  localStorage.removeItem("groupUserCount");
  localStorage.removeItem("roles");
  localStorage.removeItem("jwtExpiry");
  localStorage.removeItem("dashboardView");
  localStorage.removeItem("userName");
  localStorage.removeItem("alertLevel");
  localStorage.removeItem("app-theme");
  localStorage.removeItem("userVerified");
  localStorage.removeItem("verifiedUserUuid");
  localStorage.removeItem("ada-contrast");
  localStorage.removeItem("ada-high-contrast");
  localStorage.removeItem("analyticsEnabled");
  localStorage.removeItem("dashboardMode");
  localStorage.removeItem("dateFormat");
  localStorage.removeItem("emailMarketing");
  localStorage.removeItem("emailSecurityAlerts");
  localStorage.removeItem("emailTrainingReminders");
  localStorage.removeItem("emailWeeklyDigest");
  localStorage.removeItem("gettingStarted_explored");
  localStorage.removeItem("landingPage");
  localStorage.removeItem("language");
  localStorage.removeItem("loginNotificationsEnabled");
  localStorage.removeItem("profileVisibility");
  localStorage.removeItem("pushNotificationsEnabled");
  localStorage.removeItem("showViewSettings");
  localStorage.removeItem("theme");
  localStorage.removeItem("timeFormat");
  localStorage.removeItem("timezone");
  localStorage.removeItem("tryitout_fingerprint");

  // Clear sessionStorage (including trial reminder flag)
  sessionStorage.clear();
  clearTrialReminderFlag();

  // Clear authentication cookies by setting them to expire
  const authCookies = [
    "uuid",
    "user",
    "authToken",
    "jwt_token",
    "user_uuid",
    "sessionId",
    "refreshToken",
    "userPreferences",
    "lastLoginTime",
    "deviceFingerprint",
  ];
  authCookies.forEach((cookieName) => {
    document.cookie = `${cookieName}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; HttpOnly; SameSite=Strict`;
    document.cookie = `${cookieName}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`; // Fallback without HttpOnly for client-side cookies
  });

  // Notify UserContext to clear React state
  if (globalLogoutCallback) {
    console.log("🔔 AuthUtils: Notifying UserContext to clear state");
    globalLogoutCallback();
  }

  console.log("✅ AuthUtils: Authentication data cleared");
};

/**
 * Check if frontend authentication data exists
 */
export const hasFrontendAuthData = () => {
  // Check cookies
  const hasCookies = document.cookie
    .split(";")
    .some(
      (cookie) =>
        cookie.trim().startsWith("uuid=") || cookie.trim().startsWith("user="),
    );

  // Check localStorage
  const hasLocalStorage =
    localStorage.getItem("userName") || localStorage.getItem("jwt_token");

  return hasCookies || hasLocalStorage;
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};
